/**
 * TanStack Query hooks against the message-gateway `/memory` REST surface.
 *
 * Five operations, all identity-bound by the verified token on the server:
 *  - useMemoryList(scope?, sessionId?) — read; for the sidecar panel and the
 *    agent's `list_memories` summary.
 *  - useConfirmMemoryProposal — POST /memory; called from the inline
 *    proposal card on user-confirm.
 *  - useUpdateMemory — PATCH; rename or widen scope from the sidecar.
 *  - useForgetMemory — DELETE; called from the sidecar's forget button or
 *    the inline forget-proposal card.
 *  - usePinMemory — POST /{id}/pin; toggle pinned.
 *
 * Every mutation invalidates the list query so the sidebar reflects writes
 * immediately. The list and the inline cards are the only consumers today
 * — if a third surface lands, promote these into a domain-scoped lib.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { authFetch } from './authFetch';
import { memoryKeys } from './memoryKeys';
import {
  memoryEntryEnvelopeSchema,
  memoryListEnvelopeSchema,
  type MemoryConfirmRequest,
  type MemoryEntry,
  type MemoryScope,
} from './memoryTypes';

// authFetch's baseUrl resolves to `${getBaseApiUrl()}/${getGatewayApiVersion()}`,
// which already includes `/v1/message-gateway`. The router's prefix
// (`/v1/message-gateway/memory`) means the path appended here is just `/memory`.
const MEMORY_PATH = '/memory';

interface UseMemoryListArgs {
  scope?: MemoryScope;
  sessionId?: string | null;
  enabled?: boolean;
}

export const useMemoryList = ({ scope, sessionId, enabled = true }: UseMemoryListArgs = {}) =>
  useQuery({
    queryKey: memoryKeys.list(scope, sessionId),
    enabled,
    queryFn: async (): Promise<MemoryEntry[]> => {
      const params = new URLSearchParams();
      if (scope) params.set('scope', scope);
      if (sessionId) params.set('sessionId', sessionId);
      const qs = params.toString();
      const path = qs ? `${MEMORY_PATH}?${qs}` : MEMORY_PATH;
      const json = await authFetch<unknown>(path);
      return memoryListEnvelopeSchema.parse(json).data;
    },
  });

export const useConfirmMemoryProposal = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: MemoryConfirmRequest): Promise<MemoryEntry> => {
      const json = await authFetch<unknown>(MEMORY_PATH, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      return memoryEntryEnvelopeSchema.parse(json).data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memoryKeys.all });
    },
  });
};

interface UpdateMemoryArgs {
  memoryId: string;
  text?: string;
  scope?: MemoryScope;
  rationale?: string | null;
  sessionId?: string | null;
}

export const useUpdateMemory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      memoryId,
      text,
      scope,
      rationale,
      sessionId,
    }: UpdateMemoryArgs): Promise<MemoryEntry> => {
      const body: Record<string, unknown> = {};
      if (text !== undefined) body.text = text;
      if (scope !== undefined) body.scope = scope;
      if (rationale !== undefined) body.rationale = rationale;
      if (sessionId !== undefined) body.sessionId = sessionId;
      const json = await authFetch<unknown>(`${MEMORY_PATH}/${memoryId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      return memoryEntryEnvelopeSchema.parse(json).data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memoryKeys.all });
    },
  });
};

interface ForgetMemoryArgs {
  memoryId: string;
  sessionId?: string | null;
}

export const useForgetMemory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ memoryId, sessionId }: ForgetMemoryArgs): Promise<void> => {
      const params = new URLSearchParams();
      if (sessionId) params.set('sessionId', sessionId);
      const qs = params.toString();
      const path = qs ? `${MEMORY_PATH}/${memoryId}?${qs}` : `${MEMORY_PATH}/${memoryId}`;
      await authFetch<void>(path, { method: 'DELETE' });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memoryKeys.all });
    },
  });
};

interface PinMemoryArgs {
  memoryId: string;
  pinned: boolean;
  sessionId?: string | null;
}

export const usePinMemory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ memoryId, pinned, sessionId }: PinMemoryArgs): Promise<MemoryEntry> => {
      const params = new URLSearchParams();
      if (sessionId) params.set('sessionId', sessionId);
      const qs = params.toString();
      const path = qs ? `${MEMORY_PATH}/${memoryId}/pin?${qs}` : `${MEMORY_PATH}/${memoryId}/pin`;
      const json = await authFetch<unknown>(path, {
        method: 'POST',
        body: JSON.stringify({ pinned }),
      });
      return memoryEntryEnvelopeSchema.parse(json).data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memoryKeys.all });
    },
  });
};
